import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Search, FileText, Handshake, ArrowRight } from 'lucide-react';
import { images } from '../data/mockData';

const HowWeWork = () => {
  const process = [
    {
      icon: MessageCircle,
      step: '1',
      title: 'Start with a Conversation',
      description: 'Tell us what you\'re trying to figure out. Phone call, email, or in person. Your choice.',
      details: [
        'Completely confidential',
        'No obligation',
        'Usually 30-45 minutes',
        'We listen more than we talk'
      ]
    },
    {
      icon: Search,
      step: '2',
      title: 'We Look at Your Situation',
      description: 'If it makes sense to work together, we spend time understanding your organization and your actual situation.',
      details: [
        'On-site visit to see how things work',
        'Talk with relevant people',
        'Review what you\'re currently doing',
        'Understand your constraints and priorities'
      ]
    },
    {
      icon: FileText,
      step: '3',
      title: 'Clear Explanation',
      description: 'We explain what we found. In regular language. No thick reports.',
      details: [
        'Face-to-face discussion',
        'Clear explanation of what matters',
        'Honest assessment of risks and options',
        'Written summary you can use'
      ]
    },
    {
      icon: Handshake,
      step: '4',
      title: 'You Decide Next Steps',
      description: 'We tell you what we\'d do if we were in your shoes. You decide if that makes sense.',
      details: [
        'No pressure to do everything at once',
        'Clear priorities for your situation',
        'Honest about costs and effort',
        'Continued support available if you want it'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-stone-100 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] leading-tight mb-6">
                How We Work
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                A simple process designed to give you clarity without wasting time
              </p>
            </div>
            <div className="relative">
              <img
                src={images.hero.home}
                alt="Professional consultation"
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  {/* Step Number and Icon */}
                  <div className="lg:col-span-2 flex flex-col items-center lg:items-start">
                    <div className="w-16 h-16 bg-[#c84630] text-white rounded-full flex items-center justify-center font-bold text-2xl mb-4">
                      {item.step}
                    </div>
                    <div className="w-12 h-12 bg-[#f5e6e0] rounded-lg flex items-center justify-center">
                      <item.icon size={24} className="text-[#c84630]" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:col-span-10">
                    <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">
                      {item.title}
                    </h2>
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      {item.description}
                    </p>
                    <div className="bg-stone-50 p-6 rounded-lg">
                      <h3 className="font-semibold text-[#1e3a5f] mb-3">What this includes:</h3>
                      <ul className="space-y-2">
                        {item.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-[#c84630] mr-3">•</span>
                            <span className="text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Connecting Line (except for last item) */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute left-8 top-16 w-0.5 h-full bg-gray-300" style={{ height: 'calc(100% + 4rem)' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-12 text-center">
            What to Expect
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                Timeline
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Most things take 2-4 weeks from first conversation to final recommendations. Urgent situations move faster.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                Your Time
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Usually a few hours of your time across 2-3 meetings. We work around your schedule.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                Cost
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We discuss costs upfront. No surprises.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                Confidentiality
              </h3>
              <p className="text-gray-700 leading-relaxed">
                What we discuss and learn stays confidential.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                Follow-up Support
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Available if you want it. Not required if you don't.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">
                No Contracts
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Work with us when it makes sense. Stop when it doesn't.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Scenarios */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-12 text-center">
            Common Scenarios
          </h2>
          <div className="space-y-8">
            <div className="border-l-4 border-[#c84630] pl-6 py-2">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                "We got a notice about TAC 202 and don't know where to start"
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We explain what it requires for your situation, help with documentation, and get you ready for audits. Usually 3-4 weeks.
              </p>
            </div>
            <div className="border-l-4 border-[#c84630] pl-6 py-2">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                "We think AI could help but don't know if it's safe or how to use it"
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We show you practical uses, explain the risks, and help you start using it safely. Usually 1-2 meetings.
              </p>
            </div>
            <div className="border-l-4 border-[#c84630] pl-6 py-2">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                "Someone's trying to sell us something and we want a second opinion"
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We review what they're proposing, explain what it actually does, and tell you if it makes sense. Usually one meeting.
              </p>
            </div>
            <div className="border-l-4 border-[#c84630] pl-6 py-2">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                "We need security training our staff will actually understand"
              </h3>
              <p className="text-gray-700 leading-relaxed">
                On-site training in plain language with real examples. Usually 1-2 hours depending on group size.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Local Service Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">
            Personal, Local Service
          </h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We meet with you in person when possible. These conversations are easier face-to-face, and we can see how things actually work.
          </p>
          <p className="text-gray-700 leading-relaxed">
            We're here in West Texas. When you call, you get us. When you have a question, you reach someone who knows your situation.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to start a conversation?
          </h2>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Tell us what you're working on or what's concerning you. No commitment.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors text-lg"
          >
            Contact us
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;
